import type { MouseEvent, ReactNode } from 'react';
import { useFormat } from '@paige/format';
import type { CustomerRow, CustomerSort, SortDir } from './customer-types';
import { sortWord } from './customer-words';
import {
  AttentionCell,
  HealthCell,
  LastActiveCell,
  MoneyCell,
  NameCell,
  NextStepCell,
  PagesCell,
  type CompanyLink,
} from './customer-cells';
import './customers.css';

export interface CustomerTableProps {
  rows: readonly CustomerRow[];
  /** The UTC day it is, for health and for what is overdue. */
  today: string;
  sort: CustomerSort;
  dir: SortDir;
  /** A press on a heading the ledger sorts by. */
  onSort: (sort: CustomerSort) => void;
  onOpen: (id: string) => void;
  renderLink: CompanyLink;
}

/** The direction a heading sorts in when it is first pressed: names A to Z, numbers and days largest first. */
export function firstDir(sort: CustomerSort): SortDir {
  return sort === 'name' ? 'asc' : 'desc';
}

/** A heading the ledger sorts by: a button, with the order said to a screen reader. */
function SortHead(props: {
  sort: CustomerSort;
  current: CustomerSort;
  dir: SortDir;
  numeric?: boolean;
  onSort: (sort: CustomerSort) => void;
}): ReactNode {
  const on = props.current === props.sort;
  const spoken = on ? (props.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th scope="col" aria-sort={spoken} className={props.numeric ? 'is-numeric' : undefined}>
      <button type="button" onClick={() => props.onSort(props.sort)}>
        {sortWord[props.sort]}
        <span className="ad-sort__arrow" aria-hidden="true">
          {on ? (props.dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

function Head({ sort, dir, onSort }: CustomerTableProps): ReactNode {
  const head = { current: sort, dir, onSort };
  return (
    <thead>
      <tr>
        <SortHead sort="name" {...head} />
        <SortHead sort="paid" numeric {...head} />
        <SortHead sort="pages" numeric {...head} />
        <th scope="col">Health</th>
        <th scope="col">Needs attention</th>
        <th scope="col">Next step</th>
        <SortHead sort="lastActive" {...head} />
        <SortHead sort="signedUp" {...head} />
      </tr>
    </thead>
  );
}

/** A click anywhere on the row opens the company; a click on a link, or with a modifier, is the link's own. */
function opensRow(e: MouseEvent<HTMLElement>): boolean {
  const onLink = (e.target as HTMLElement).closest('a, button') !== null;
  return !onLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0;
}

function Row(props: { row: CustomerRow } & CustomerTableProps): ReactNode {
  const { row, today } = props;
  const f = useFormat();
  return (
    <tr className="ad-cu-row" onClick={(e) => (opensRow(e) ? props.onOpen(row.id) : undefined)}>
      <th scope="row">
        <NameCell row={row} renderLink={props.renderLink} />
      </th>
      <td className="is-numeric">
        <MoneyCell row={row} />
      </td>
      <td className="is-numeric">
        <PagesCell row={row} />
      </td>
      <td>
        <HealthCell row={row} today={today} />
      </td>
      <td>
        <AttentionCell row={row} />
      </td>
      <td>
        <NextStepCell row={row} today={today} />
      </td>
      <td>
        <LastActiveCell row={row} />
      </td>
      <td>{f.date(row.signedUpAt)}</td>
    </tr>
  );
}

/**
 * One page of customers as a table. Only the headings the ledger sorts by are buttons; the whole row
 * opens the company, and the name is a real link (so it opens in a new tab like any link).
 */
export function CustomerTable(props: CustomerTableProps): ReactNode {
  return (
    <div className="ad-cu-scroll">
      <table className="ad-sort ad-cu-table">
        <Head {...props} />
        <tbody>
          {props.rows.map((row) => (
            <Row key={row.id} row={row} {...props} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
